/**
 * ============================================================================
 * Iron Meridian - Shared Tooltip Component
 * ============================================================================
 * 
 * Reusable tooltip component with consistent styling.
 * Supports delayed appearance, cursor following, and viewport clamping.
 * Uses the same visual style as CountryTooltip for consistency.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'

// ============================================================================
// TOOLTIP CONTEXT FOR GLOBAL STATE (optional)
// ============================================================================

const TOOLTIP_DELAY = 250 // ms before tooltip appears
const TOOLTIP_OFFSET = { x: 12, y: 12 }

// ============================================================================
// TOOLTIP CONTENT COMPONENT
// ============================================================================

function TooltipContent({ content, position, isVisible }) {
  const tooltipRef = useRef(null)
  const [adjustedPosition, setAdjustedPosition] = useState(position)

  // Adjust position to stay within viewport
  useEffect(() => {
    if (!tooltipRef.current || !isVisible) return

    const rect = tooltipRef.current.getBoundingClientRect()
    let newX = position.x + TOOLTIP_OFFSET.x
    let newY = position.y + TOOLTIP_OFFSET.y

    // Check right edge
    if (newX + rect.width > window.innerWidth - 10) {
      newX = position.x - rect.width - TOOLTIP_OFFSET.x
    }

    // Check bottom edge
    if (newY + rect.height > window.innerHeight - 10) {
      newY = position.y - rect.height - TOOLTIP_OFFSET.y
    }

    // Clamp to viewport
    newX = Math.max(10, newX)
    newY = Math.max(10, newY)

    setAdjustedPosition({ x: newX, y: newY })
  }, [position, isVisible])

  if (!isVisible) return null

  const style = {
    left: adjustedPosition.x,
    top: adjustedPosition.y,
  }

  return createPortal(
    <div
      ref={tooltipRef}
      className="fixed z-[100] pointer-events-none animate-tooltip-in"
      style={style}
      role="tooltip"
    >
      <div className="bg-war-room-800/95 backdrop-blur-md border border-war-room-600/50 rounded-lg shadow-2xl px-3 py-2 max-w-xs">
        {typeof content === 'string' ? (
          <p className="text-sm text-gray-200">{content}</p>
        ) : (
          content
        )}
      </div>
    </div>,
    document.body
  )
}

// ============================================================================
// MAIN TOOLTIP WRAPPER COMPONENT
// ============================================================================

export default function Tooltip({ 
  children, 
  content, 
  delay = TOOLTIP_DELAY,
  disabled = false 
}) {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const timeoutRef = useRef(null)
  const containerRef = useRef(null)

  const handleMouseEnter = useCallback(() => {
    if (disabled) return
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true)
    }, delay)
  }, [delay, disabled])

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsVisible(false)
  }, [])

  const handleMouseMove = useCallback((e) => {
    setPosition({ x: e.clientX, y: e.clientY })
  }, [])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Handle reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined' 
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
    : false

  if (!content) return children

  return (
    <>
      <span
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        className="inline-block"
      >
        {children}
      </span>
      <TooltipContent
        content={content}
        position={position}
        isVisible={isVisible && !prefersReducedMotion}
      />
    </>
  )
}

// ============================================================================
// SPECIALIZED TOOLTIP COMPONENTS
// ============================================================================

/**
 * Stat Tooltip - For military, economy, tech, stability stats
 */
export function StatTooltip({ stat, value, children }) {
  const statInfo = {
    military: {
      icon: '⚔️',
      name: 'Military Power',
      desc: 'Combat strength for attacks and defense. Higher values improve battle outcomes.',
      color: 'text-red-400'
    },
    economy: {
      icon: '💰',
      name: 'Economy',
      desc: 'Resource generation per turn. Funds military upkeep, buildings, and research.',
      color: 'text-yellow-400'
    },
    technology: {
      icon: '🔬',
      name: 'Technology',
      desc: 'Force multiplier in combat. Higher tech provides tactical advantages.',
      color: 'text-blue-400'
    },
    stability: {
      icon: '🏛️',
      name: 'Stability',
      desc: 'Internal order. Low stability risks rebellions and reduces efficiency.',
      color: 'text-green-400'
    }
  }

  const info = statInfo[stat] || { icon: '📊', name: stat, desc: '', color: 'text-gray-400' }

  const content = (
    <div className="space-y-1">
      <div className={`flex items-center gap-2 font-medium ${info.color}`}>
        <span>{info.icon}</span>
        <span>{info.name}</span>
        <span className="ml-auto font-mono">{value}</span>
      </div>
      <p className="text-xs text-gray-400 leading-relaxed">{info.desc}</p>
    </div>
  )

  return <Tooltip content={content}>{children}</Tooltip>
}

/**
 * Terrain Tooltip - For terrain types with combat modifiers
 */
export function TerrainTooltip({ terrain, children }) {
  const terrainInfo = {
    plains: { 
      icon: '🌾', 
      name: 'Plains', 
      modifier: 0, 
      desc: 'Open terrain with no defensive advantage. Ideal for rapid movement.' 
    },
    mountains: { 
      icon: '⛰️', 
      name: 'Mountains', 
      modifier: 25, 
      desc: 'Rugged terrain providing excellent defensive positions. Difficult to assault.' 
    },
    forest: { 
      icon: '🌲', 
      name: 'Forest', 
      modifier: 15, 
      desc: 'Dense woodland offering cover and concealment for defenders.' 
    },
    desert: { 
      icon: '🏜️', 
      name: 'Desert', 
      modifier: -10, 
      desc: 'Harsh conditions that strain supply lines and exhaust attacking forces.' 
    },
    tundra: { 
      icon: '❄️', 
      name: 'Tundra', 
      modifier: 10, 
      desc: 'Frozen wasteland that slows invaders and favors prepared defenders.' 
    },
    coastal: { 
      icon: '🏖️', 
      name: 'Coastal', 
      modifier: 5, 
      desc: 'Seaside territory with naval access. Enables amphibious operations.' 
    },
    jungle: { 
      icon: '🌴', 
      name: 'Jungle', 
      modifier: 20, 
      desc: 'Dense tropical terrain that severely hampers offensive operations.' 
    },
    urban: { 
      icon: '🏙️', 
      name: 'Urban', 
      modifier: 30, 
      desc: 'City warfare heavily favors defenders. Building-to-building fighting is costly.' 
    }
  }

  const info = terrainInfo[terrain] || terrainInfo.plains

  const content = (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-lg">{info.icon}</span>
        <span className="font-medium text-gray-200">{info.name}</span>
        {info.modifier !== 0 && (
          <span className={`ml-auto text-xs font-mono px-1.5 py-0.5 rounded ${
            info.modifier > 0 
              ? 'bg-green-500/20 text-green-400' 
              : 'bg-red-500/20 text-red-400'
          }`}>
            {info.modifier > 0 ? '+' : ''}{info.modifier}% defense
          </span>
        )}
      </div>
      <p className="text-xs text-gray-400 leading-relaxed">{info.desc}</p>
    </div>
  )

  return <Tooltip content={content}>{children}</Tooltip>
}

/**
 * Combat Odds Tooltip - Shows estimated battle outcome
 */
export function CombatOddsTooltip({ 
  attackerStrength, 
  defenderStrength, 
  terrainModifier = 0,
  techAdvantage = 0,
  children 
}) {
  // Simple odds calculation (matches V2 combat engine logic)
  const defenderEffective = defenderStrength * (1 + terrainModifier / 100)
  const ratio = attackerStrength / Math.max(defenderEffective, 1)
  
  let outcome, confidence, color
  if (ratio >= 2.0) {
    outcome = 'Likely Victory'
    confidence = 'High'
    color = 'text-green-400'
  } else if (ratio >= 1.3) {
    outcome = 'Probable Victory'
    confidence = 'Medium'
    color = 'text-green-300'
  } else if (ratio >= 0.8) {
    outcome = 'Uncertain'
    confidence = 'Low'
    color = 'text-yellow-400'
  } else if (ratio >= 0.5) {
    outcome = 'Probable Defeat'
    confidence = 'Medium'
    color = 'text-orange-400'
  } else {
    outcome = 'Likely Defeat'
    confidence = 'High'
    color = 'text-red-400'
  }

  const content = (
    <div className="space-y-2 min-w-[180px]">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500 uppercase">Estimated Outcome</span>
        <span className={`font-medium ${color}`}>{outcome}</span>
      </div>
      <div className="space-y-1 text-xs">
        <div className="flex justify-between">
          <span className="text-gray-400">Attacker strength</span>
          <span className="font-mono text-gray-200">{attackerStrength}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">Defender effective</span>
          <span className="font-mono text-gray-200">{Math.round(defenderEffective)}</span>
        </div>
        {terrainModifier !== 0 && (
          <div className="flex justify-between">
            <span className="text-gray-400">Terrain modifier</span>
            <span className={terrainModifier > 0 ? 'text-red-400' : 'text-green-400'}>
              {terrainModifier > 0 ? '+' : ''}{terrainModifier}%
            </span>
          </div>
        )}
        {techAdvantage !== 0 && (
          <div className="flex justify-between">
            <span className="text-gray-400">Tech advantage</span>
            <span className={techAdvantage > 0 ? 'text-green-400' : 'text-red-400'}>
              {techAdvantage > 0 ? '+' : ''}{techAdvantage}%
            </span>
          </div>
        )}
      </div>
      <div className="pt-1 border-t border-war-room-600/50 text-xs text-gray-500">
        Confidence: {confidence}
      </div>
    </div>
  )

  return <Tooltip content={content}>{children}</Tooltip>
}

/**
 * Modifier Tooltip - For buffs/debuffs
 */
export function ModifierTooltip({ name, value, description, isPositive, children }) {
  const content = (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className={`font-medium ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
          {isPositive ? '+' : ''}{value}
        </span>
        <span className="text-gray-200">{name}</span>
      </div>
      {description && (
        <p className="text-xs text-gray-400">{description}</p>
      )}
    </div>
  )

  return <Tooltip content={content}>{children}</Tooltip>
}
